import { useEffect, useRef, useState } from 'react';
import { Animated, Easing } from 'react-native';

import { scale } from '@components/frame';
import { TRANSITION_DURATION_MS, TRANSITION_OFFSET } from '@components/tenantStatus/onboardingWizard/constants';
import type { WizardStepTransitionProps } from '@components/tenantStatus/onboardingWizard/components/WizardStepTransition';

type Layer = { key: number; node: React.ReactNode };

export type UseManageWizardStepTransitionParams = Pick<WizardStepTransitionProps, 'stepKey' | 'children'>;

export const useManageWizardStepTransition = ({ stepKey, children }: UseManageWizardStepTransitionParams) => {
  const [activeSlot, setActiveSlot] = useState<0 | 1>(0);
  const [content, setContent] = useState<[Layer, Layer]>(() => [
    { key: stepKey, node: children },
    { key: -1, node: null },
  ]);

  const opacityA = useRef(new Animated.Value(1)).current;
  const opacityB = useRef(new Animated.Value(0)).current;
  const translateA = useRef(new Animated.Value(0)).current;
  const translateB = useRef(new Animated.Value(0)).current;

  const runningAnimation = useRef<Animated.CompositeAnimation | null>(null);
  const mounted = useRef(true);

  useEffect(
    () => () => {
      mounted.current = false;
      runningAnimation.current?.stop();
      runningAnimation.current = null;
    },
    [],
  );

  useEffect(() => {
    const inactiveSlot = (activeSlot === 0 ? 1 : 0) as 0 | 1;
    const opacities = [opacityA, opacityB] as const;
    const translates = [translateA, translateB] as const;
    const activeOpacity = opacities[activeSlot];
    const inactiveOpacity = opacities[inactiveSlot];
    const activeTranslate = translates[activeSlot];
    const inactiveTranslate = translates[inactiveSlot];

    const activeKey = content[activeSlot].key;

    if (activeKey === stepKey) {
      if (runningAnimation.current) {
        runningAnimation.current.stop();
        runningAnimation.current = null;
        activeOpacity.setValue(1);
        activeTranslate.setValue(0);
        inactiveOpacity.setValue(0);
      }
      if (content[activeSlot].node !== children) {
        setContent((prev) => {
          const next = [...prev] as [Layer, Layer];
          next[activeSlot] = { key: stepKey, node: children };
          return next;
        });
      }
      return;
    }

    // Animation already heading toward this stepKey — don't restart on incidental re-renders.
    if (runningAnimation.current && content[inactiveSlot].key === stepKey) return;

    // Mid-flight redirect: a running animation is heading to a step the user no longer wants.
    // Snap-promote the inactive (in-progress incoming) slot to active. The next effect run
    // (triggered by setActiveSlot) starts the fresh transition from there, preserving the
    // "I clicked through several steps" visual rhythm without restarting from the original
    // displayed step.
    if (runningAnimation.current) {
      runningAnimation.current.stop();
      runningAnimation.current = null;
      activeOpacity.setValue(0);
      inactiveOpacity.setValue(1);
      inactiveTranslate.setValue(0);
      setActiveSlot(inactiveSlot);
      return;
    }

    const direction = stepKey > activeKey ? 1 : -1;
    const offset = scale(TRANSITION_OFFSET);

    setContent((prev) => {
      const next = [...prev] as [Layer, Layer];
      next[inactiveSlot] = { key: stepKey, node: children };
      return next;
    });

    activeOpacity.setValue(1);
    activeTranslate.setValue(0);
    inactiveOpacity.setValue(0);
    inactiveTranslate.setValue(direction * offset);

    const timing = (value: Animated.Value, toValue: number) =>
      Animated.timing(value, {
        toValue,
        duration: TRANSITION_DURATION_MS,
        easing: Easing.out(Easing.cubic),
        useNativeDriver: true,
      });

    const animation = Animated.parallel([
      timing(activeOpacity, 0),
      timing(activeTranslate, -direction * offset),
      timing(inactiveOpacity, 1),
      timing(inactiveTranslate, 0),
    ]);
    runningAnimation.current = animation;
    animation.start(({ finished }) => {
      if (!mounted.current || runningAnimation.current !== animation) return;
      runningAnimation.current = null;
      if (!finished) return;
      setActiveSlot(inactiveSlot);
    });
  }, [stepKey, children, activeSlot, content, opacityA, opacityB, translateA, translateB]);

  return { activeSlot, content, opacityA, opacityB, translateA, translateB };
};
